import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { REFRESH_LEEWAY_MS, SessionService } from './services/session.service';

/**
 * Ciclo de vida de la sesión en el navegador.
 *
 * 1) Refresh SILENCIOSO del access token: se programa según el `exp` real del JWT (15 min en el
 *    backend) y se renueva 60 s antes de vencer, sin molestar al usuario. Mientras haya actividad
 *    la sesión se mantiene viva indefinidamente (el refresh token rota y renueva sus 8 h).
 * 2) Cierre por INACTIVIDAD: si no hay mouse/teclado/scroll/touch durante INACTIVITY_LIMIT_MS
 *    se muestra el aviso con cuenta regresiva de WARNING_MS; si el usuario no hace nada, logout.
 *    Cualquier actividad reinicia el contador. La navegación entre páginas NO reinicia nada.
 * 3) Si el backend rechaza el refresh (revocado/expirado) → SessionService manda al login.
 */

const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 min sin actividad → aviso
const WARNING_MS = 60 * 1000; // 60 s de cuenta regresiva antes del logout
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel'
] as const;
const ACTIVITY_THROTTLE_MS = 5_000;

interface SessionContextType {
  /** Reinicia el contador de inactividad y (re)programa el refresh. Llamar tras login. */
  startSessionTimers: () => void;
  /** Cierra sesión revocando el refresh en el backend. */
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isAtLogin = location.pathname.includes('/auth/');

  const [showAlert, setShowAlert] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_MS / 1000);

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivity = useRef<number>(Date.now());
  const lastActivityHandled = useRef<number>(0);

  const clearRefresh = () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
  };
  const clearIdle = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdownTimer.current) clearInterval(countdownTimer.current);
    idleTimer.current = null;
    countdownTimer.current = null;
  };

  const logout = useCallback(async () => {
    clearRefresh();
    clearIdle();
    setShowAlert(false);
    await SessionService.logout();
  }, []);

  /** Programa el refresh silencioso en función del exp del JWT actual. */
  const scheduleRefresh = useCallback(() => {
    clearRefresh();
    if (!SessionService.hasSession()) return;
    const msLeft = SessionService.msUntilExpiry();
    // Sin exp legible: refrescar en 10 min por defecto. Ya vencido o a punto: refrescar ya.
    const delay = msLeft === null ? 10 * 60 * 1000 : Math.max(0, msLeft - REFRESH_LEEWAY_MS);
    refreshTimer.current = setTimeout(async () => {
      const token = await SessionService.refreshAccessToken();
      if (token) scheduleRefresh();
      else if (SessionService.hasSession()) {
        // error transitorio (red/5xx): reintentar en 30 s mientras el access siga vivo
        refreshTimer.current = setTimeout(scheduleRefresh, 30_000);
      }
    }, delay);
  }, []);

  /** Arranca el contador de inactividad desde cero. */
  const armIdle = useCallback(() => {
    clearIdle();
    setShowAlert(false);
    setCountdown(WARNING_MS / 1000);
    if (!SessionService.hasSession()) return;
    idleTimer.current = setTimeout(() => {
      setShowAlert(true);
      let left = WARNING_MS / 1000;
      countdownTimer.current = setInterval(() => {
        left -= 1;
        setCountdown(left);
        if (left <= 0) {
          clearIdle();
          void SessionService.logout();
        }
      }, 1000);
    }, INACTIVITY_LIMIT_MS);
  }, []);

  const startSessionTimers = useCallback(() => {
    lastActivity.current = Date.now();
    scheduleRefresh();
    armIdle();
  }, [scheduleRefresh, armIdle]);

  // Actividad del usuario → reinicia inactividad (throttled). Si el aviso está visible, no
  // lo cierra solo: hay que dar clic en "Seguir conectado" para evitar cierres accidentales.
  useEffect(() => {
    if (isAtLogin) return;
    const onActivity = () => {
      const now = Date.now();
      lastActivity.current = now;
      if (showAlert) return;
      if (now - lastActivityHandled.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityHandled.current = now;
      armIdle();
    };
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
  }, [isAtLogin, showAlert, armIdle]);

  // Al volver a la pestaña tras estar en segundo plano: si el token venció mientras tanto,
  // refrescar de inmediato; si llevamos más de INACTIVITY_LIMIT_MS sin actividad, mostrar aviso.
  useEffect(() => {
    if (isAtLogin) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !SessionService.hasSession()) return;
      const msLeft = SessionService.msUntilExpiry();
      if (msLeft !== null && msLeft < REFRESH_LEEWAY_MS) {
        void SessionService.refreshAccessToken().then((t) => t && scheduleRefresh());
      }
      if (Date.now() - lastActivity.current > INACTIVITY_LIMIT_MS + WARNING_MS) {
        void SessionService.logout();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAtLogin, scheduleRefresh]);

  // Otra pestaña refrescó o cerró sesión → sincronizar.
  useEffect(() => {
    const onRefreshed = () => scheduleRefresh();
    const onExpired = () => {
      clearRefresh();
      clearIdle();
      setShowAlert(false);
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key.endsWith('token')) {
        if (e.newValue) scheduleRefresh();
        else onExpired();
      }
    };
    window.addEventListener('raptor:session-refreshed', onRefreshed);
    window.addEventListener('raptor:session-expired', onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('raptor:session-refreshed', onRefreshed);
      window.removeEventListener('raptor:session-expired', onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, [scheduleRefresh]);

  // Montaje / cambio login↔app. NO depende de location.pathname completo: navegar no reinicia.
  useEffect(() => {
    if (!isAtLogin && SessionService.hasSession()) {
      startSessionTimers();
    } else {
      clearRefresh();
      clearIdle();
      setShowAlert(false);
    }
    return () => {
      clearRefresh();
      clearIdle();
    };
  }, [isAtLogin, startSessionTimers]);

  const stay = () => {
    lastActivity.current = Date.now();
    armIdle();
  };

  const mm = Math.floor(countdown / 60);
  const ss = countdown % 60;

  return (
    <SessionContext.Provider value={{ startSessionTimers, logout }}>
      {children}
      {showAlert && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-dark text-white border border-secondary">
              <div className="modal-header border-0">
                <h5 className="modal-title text-warning">¿Sigues ahí?</h5>
              </div>
              <div className="modal-body text-center">
                <p className="mb-1">Llevas {INACTIVITY_LIMIT_MS / 60000} minutos sin actividad.</p>
                <p className="text-muted fs-7">
                  Por seguridad cerraremos la sesión si no respondes.
                </p>
                <div className="fs-1 fw-bold text-primary my-4">
                  {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
                </div>
              </div>
              <div className="modal-footer border-0 d-flex justify-content-between">
                <button
                  type="button"
                  className="btn btn-light-danger"
                  onClick={() => void logout()}
                >
                  Salir
                </button>
                <button type="button" className="btn btn-primary" onClick={stay} autoFocus>
                  Seguir conectado
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </SessionContext.Provider>
  );
};

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession debe ser utilizado dentro de un SessionProvider');
  }
  return context;
};
